
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { DateTime } from 'luxon';
import { addBorderAroundTable, addStylingToHorizontalHeader, downloadFile, setCellFontSizeAndAlignement } from '@/utils/exceljs-helpers';
import { copyToClipboard } from '@/utils/general';
import { dateSort, valueSort } from '@/utils/sort';
import Toast from '@/utils/toast';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import IconButton from '@/components/shared/suites/IconButton.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import TextField from '@/components/shared/TextField.vue';
import * as ExcelJS from 'exceljs';
import '@/validation-rules';

const namespace: string = 'subsidies';

@Component<SubsidyCodes>({
	components: {
		TextField,
		TableHeaders,
		TextButton,
		IconButton,
		InfoBanner,
		WarningModal,
		ValidationObserver,
		ValidationProvider
	}
})
export default class SubsidyCodes extends Vue {
	@Action('generateSubsidyCodes', { namespace }) private generateSubsidyCodes!: ({ subsidyId, amount }: { subsidyId: number; amount: number }) => Promise<void>;
	@Action('deleteSubsidyCode', { namespace }) private deleteSubsidyCode!: ({ subsidyId, codeId }: { subsidyId: number; codeId: number }) => Promise<void>;

	@Prop({ type: Object, required: true }) private subsidy!: Subsidy;
	@Prop({ type: Boolean, default: false }) private isConfiguringSubsidy!: boolean;

	private isGenerating: boolean = false;
	private isDeleting: boolean = false;
	private showDeleteModal: boolean = false;
	private codesAmount: number | null = null;
	private codeToDelete: SubsidyCode | null = null;
	private copiedCodeId: number | null = null;
	private sortColumn: string = 'created_at';
	private sortDirections: { [key: string]: 'asc' | 'desc' } = {
		created_at: 'desc',
		is_active: 'desc',
		salata_account_full_name: 'desc'
	};

	private get tableHeaders(): TableHeader[] {
		return [
			{ text: this.$t('settings.subsidies.codes.table.code') },
			{ text: this.$t('settings.subsidies.codes.table.created_at'), name: 'created_at', sort: this.sortDirections.created_at },
			{ text: this.$t('settings.subsidies.codes.table.status'), name: 'is_active', sort: this.sortDirections.is_active },
			{ text: this.$t('settings.subsidies.codes.table.full_name'), name: 'salata_account_full_name', sort: this.sortDirections.salata_account_full_name },
			{ text: this.$t('settings.subsidies.codes.table.email'), name: 'email' },
			{ text: this.$t('settings.subsidies.codes.table.delete'), class: 'centered' }
		];
	}

	private get sortedCodes(): SubsidyCode[] {
		const codes = [...this.subsidy.codes || []];

		return codes.sort((a, b) => {
			if (this.sortColumn === 'created_at') {
				return dateSort(a.created_at, b.created_at, this.sortDirections.created_at === 'asc');
			}
			else if (this.sortColumn === 'is_active') {
				return valueSort(a, b, 'is_active', this.sortDirections.is_active === 'asc');
			}
			else if (this.sortColumn === 'salata_account_full_name') {
				const aName = a.salata_account_full_name || '';
				const bName = b.salata_account_full_name || '';

				// Put empty strings at the bottom
				if (!aName && !bName) {
					return 0;
				}
				if (!aName) {
					return 1;
				}
				if (!bName) {
					return -1;
				}

				return this.sortDirections.salata_account_full_name === 'asc'
					? aName.localeCompare(bName)
					: bName.localeCompare(aName);
			}
			return 0;
		});
	}

	private get showWarningBanner(): boolean {
		return this.subsidy.target_users === 'all' && !!this.sortedCodes.length;
	}

	/**
	 * Toggle sort for the specified column - sortedCodes computed property will react automatically
	 *
	 * @param {string} columnName - The column name to sort by
	 * @return {void}
	 */
	private toggleSort(columnName: string): void {
		this.sortColumn = columnName;
		this.sortDirections[columnName] = this.sortDirections[columnName] === 'asc' ? 'desc' : 'asc';
	}

	/**
	 * Generate the subsidy codes based on the specified amount.
	 *
	 * @returns {Promise<void>}
	 */
	private async generateCodes(): Promise<void> {
		const observer = this.$refs.observer as any;
		const isValid = await observer.validate();

		if (!isValid || !this.subsidy.id) {
			return;
		}

		this.isGenerating = true;
		try {
			await this.generateSubsidyCodes({ subsidyId: this.subsidy.id, amount: this.codesAmount! });

			// Reset codes amount value and emit event to parent to refetch subsidies (which includes codes)
			this.codesAmount = null;
			observer.reset();
			this.$emit('refetch-subsidies');
			Toast.success(this.$t('settings.subsidies.codes.success_generate', { count: this.codesAmount }));
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.subsidies.codes.error_generate', { errorMessage }));
		}
		finally {
			this.isGenerating = false;
		}
	}

	private openDeleteModal(code: SubsidyCode): void {
		this.codeToDelete = code;
		this.showDeleteModal = true;
	}

	private closeDeleteModal(): void {
		this.showDeleteModal = false;
		this.codeToDelete = null;
	}

	private async copyCodeToClipboard(code: string, codeId: number): Promise<void> {
		await copyToClipboard(code, () => {
			this.copiedCodeId = codeId;
			setTimeout(() => {
				this.copiedCodeId = null;
			}, 2000);
		});
	}

	/**
	 * Delete a subsidy code, show a success or error message, and emit an event to refetch subsidies.
	 *
	 * @return {Promise<void>}
	 */
	private async confirmDelete(): Promise<void> {
		if (!this.codeToDelete || !this.subsidy.id) {
			return;
		}

		this.isDeleting = true;
		try {
			setTimeout(async () => {
				await this.deleteSubsidyCode({ subsidyId: this.subsidy.id!, codeId: this.codeToDelete!.id });

				// Emit event to parent to refetch subsidies (which includes codes)
				this.$emit('refetch-subsidies');

				Toast.success(this.$t('settings.subsidies.codes.success_delete', { code: this.codeToDelete!.code }));

				this.closeDeleteModal();
			}, 400);
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.subsidies.codes.error_delete', { errorMessage }));
		}
		finally {
			this.isDeleting = false;
		}
	}

	/**
	 * Export the subsidy codes (in current sort order) to an Excel file.
	 *
	 * @return {Promise<void>}
	 */
	private async exportCodes(): Promise<void> {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet(this.$t('settings.subsidies.codes.export.title'), { pageSetup: { orientation: 'portrait', fitToPage: true } });

		worksheet.columns = [
			{ header: this.$t('settings.subsidies.codes.table.code'), key: 'code', width: 30 },
			{ header: this.$t('settings.subsidies.codes.table.created_at'), key: 'created_at', width: 20 },
			{ header: this.$t('settings.subsidies.codes.table.status'), key: 'status', width: 15 },
			{ header: this.$t('settings.subsidies.codes.table.full_name'), key: 'full_name', width: 30 },
			{ header: this.$t('settings.subsidies.codes.table.email'), key: 'email', width: 40 }
		];

		const rows = this.sortedCodes.map((code) => ({
			code: code.code,
			created_at: code.created_at ? DateTime.fromISO(code.created_at).setZone('UTC', { keepLocalTime: true }).toFormat(this.$t('settings.subsidies.codes.table.date_format')) : '',
			status: code.is_active ? this.$t('settings.subsidies.codes.table.status_active') : this.$t('settings.subsidies.codes.table.status_inactive'),
			full_name: code.salata_account_full_name || '',
			email: code.salata_account_email || ''
		}));

		worksheet.addRows(rows);

		setCellFontSizeAndAlignement(worksheet, 14, { vertical: 'top', horizontal: 'left' });
		addStylingToHorizontalHeader(worksheet);
		addBorderAroundTable(worksheet, 1, worksheet.rowCount);

		const buffer = await workbook.xlsx.writeBuffer();
		downloadFile(buffer, this.$t('settings.subsidies.codes.export.file_name', { date: DateTime.now().toFormat(this.$t('settings.subsidies.codes.export.date_format')) }));
	}
}
